import { useMemo, useRef, useState, type ReactElement } from 'react';
import { Caption1, Input, Spinner, Text, Tooltip, tokens } from '@fluentui/react-components';
import { AppsRegular, CheckmarkRegular, LaptopRegular, PeopleRegular, PersonRegular, SearchRegular } from '@fluentui/react-icons';
import { useApi } from '../../api/useApi';
import { useAsync } from '../../api/useAsync';
import type { GroupMemberKind, GroupSummaryDto } from '../../api/types';
import './groups.css';

interface TypeCol {
  kind: GroupMemberKind;
  icon: ReactElement;
  label: string;
  field: 'userCount' | 'groupCount' | 'deviceCount' | 'appCount';
}

type SortKey = 'name' | 'userCount' | 'groupCount' | 'deviceCount' | 'appCount';

const TYPE_COLS: TypeCol[] = [
  { kind: 'user', icon: <PersonRegular />, label: 'User', field: 'userCount' },
  { kind: 'group', icon: <PeopleRegular />, label: 'Gruppen', field: 'groupCount' },
  { kind: 'device', icon: <LaptopRegular />, label: 'Geräte', field: 'deviceCount' },
  { kind: 'servicePrincipal', icon: <AppsRegular />, label: 'Apps', field: 'appCount' },
];

/** Eine Typ-Spalte (Count) mit Hover-Liste der Einträge — eine Zeile je Eintrag. */
function CountCell({ group, col }: { group: GroupSummaryDto; col: TypeCol }) {
  const api = useApi();
  const [names, setNames] = useState<string[] | null>(null);
  const count = group[col.field];

  async function load(visible: boolean) {
    if (!visible || names !== null) return;
    try {
      const detail = await api.getGroup(group.id);
      setNames(detail.members.filter((m) => m.kind === col.kind).map((m) => m.displayName));
    } catch {
      setNames([]);
    }
  }

  const content =
    names === null ? (
      'Lädt…'
    ) : names.length === 0 ? (
      `Keine ${col.label}`
    ) : (
      <div className="pii">
        {names.map((n, i) => (
          <div key={i}>{n}</div>
        ))}
      </div>
    );

  return (
    <Tooltip relationship="description" content={content} onVisibleChange={(_, d) => void load(d.visible)}>
      <span
        className={count === 0 ? 'grp-cell zero' : 'grp-cell'}
        aria-label={`${col.label}: ${count}`}
        onClick={(e) => e.stopPropagation()}
      >
        {col.icon}
        {count}
      </span>
    </Tooltip>
  );
}

interface Props {
  selected: Set<string>;
  onToggle: (group: GroupSummaryDto) => void;
  title?: string;
}

/** Linke Auswahlliste: Gruppen mit Typ-Spalten (User/Gruppen/Geräte/Apps), Suche, Mehrfachauswahl. */
export function GroupList({ selected, onToggle, title = 'Gruppen' }: Props) {
  const api = useApi();
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const timer = useRef<number | undefined>(undefined);

  function onSearch(value: string) {
    setSearch(value);
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setQuery(value.trim()), 300);
  }

  const groups = useAsync(() => api.listGroups(query || undefined), [api, query]);
  const items = groups.data ?? [];

  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  function toggleSort(key: SortKey) {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'name' ? 'asc' : 'desc');
    }
  }

  const sorted = useMemo(() => {
    const arr = [...items];
    arr.sort((a, b) => {
      const cmp =
        sortKey === 'name'
          ? a.displayName.localeCompare(b.displayName, 'de', { sensitivity: 'base' })
          : a[sortKey] - b[sortKey];
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [items, sortKey, sortDir]);

  const sortInd = sortDir === 'asc' ? '▲' : '▼';

  return (
    <div className="grp-panel grp-list-panel">
      <div className="grp-panel-head">
        <span className="grp-panel-title">{title}</span>
        {items.length ? (
          <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
            {selected.size}/{items.length} gewählt
          </Caption1>
        ) : null}
      </div>

      <Input
        contentBefore={<SearchRegular />}
        placeholder="Gruppe suchen…"
        value={search}
        onChange={(_, d) => onSearch(d.value)}
      />

      {groups.loading ? (
        <div className="grp-empty">
          <Spinner size="tiny" label="Lade Gruppen…" />
        </div>
      ) : groups.error ? (
        <div className="grp-empty">
          <Text>{groups.error.message}</Text>
        </div>
      ) : items.length === 0 ? (
        <div className="grp-empty">Keine Gruppen gefunden.</div>
      ) : (
        <>
          <div className="grp-list-head">
            <span />
            <button className={sortKey === 'name' ? 'grp-sort active' : 'grp-sort'} onClick={() => toggleSort('name')}>
              Gruppe
              {sortKey === 'name' ? <span className="grp-sort-ind">{sortInd}</span> : null}
            </button>
            {TYPE_COLS.map((c) => (
              <Tooltip key={c.field} relationship="label" content={`${c.label} — sortieren`}>
                <button
                  className={sortKey === c.field ? 'grp-sort grp-sort-col active' : 'grp-sort grp-sort-col'}
                  onClick={() => toggleSort(c.field)}
                >
                  {c.icon}
                  {sortKey === c.field ? <span className="grp-sort-ind">{sortInd}</span> : null}
                </button>
              </Tooltip>
            ))}
          </div>

          <div className="grp-list">
            {sorted.map((g) => {
              const isSelected = selected.has(g.id);
              return (
                <div
                  key={g.id}
                  role="button"
                  tabIndex={0}
                  aria-pressed={isSelected}
                  className={isSelected ? 'grp-row selected' : 'grp-row'}
                  onClick={() => onToggle(g)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onToggle(g);
                    }
                  }}
                >
                  <span className="grp-row-check">{isSelected ? <CheckmarkRegular /> : null}</span>
                  <span className="grp-row-main">
                    <span className="grp-row-name">{g.displayName}</span>
                    <span className="grp-row-sub">{g.groupType}</span>
                  </span>
                  {TYPE_COLS.map((c) => (
                    <CountCell key={c.field} group={g} col={c} />
                  ))}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
